import { useMemo, useState } from "react";
import { Calendar, ClipboardList, FileText, Home, ListTree, Package } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { formatIsoDateBr, formatNumber } from "@/utils/format";
import {
  buildSolicitacaoObraLine,
  type SolicitacaoCard,
} from "@/services/suprimentos";
import { SolicitacaoItensModal } from "./SolicitacaoItensModal";

type PipelineStage = "suprimentos" | "gestor";

type PipelineRow = SolicitacaoCard & { stage: PipelineStage };

const STAGE_LABEL: Record<PipelineStage, string> = {
  suprimentos: "Aprov. Suprimentos",
  gestor: "Aprov. Gestor",
};

function PipelineCard({
  c,
  onVerItens,
}: {
  c: PipelineRow;
  onVerItens: () => void;
}) {
  const n = c.itemCount ?? 0;
  const obraLine = buildSolicitacaoObraLine(c);
  const ccText = (c.costCenterName ?? "").trim();
  const obraSectionValue = obraLine !== "Obra —" ? obraLine : ccText || "—";
  const itemBadge = n === 1 ? "1 item" : `${formatNumber(n)} itens`;

  return (
    <div className="bg-white rounded-xl border border-slate-200/90 shadow-sm p-3 text-left flex flex-col gap-3 min-h-0">
      <div className="grid grid-cols-[2.25rem_minmax(0,1fr)_auto] items-center gap-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-violet-50 border border-violet-100 flex-shrink-0">
          <FileText className="w-4 h-4 text-violet-700" strokeWidth={2} aria-hidden />
        </div>
        <p className="min-w-0 text-center text-[15px] sm:text-base font-bold text-violet-700 tabular-nums leading-tight truncate px-0.5">
          SC {c.purchaseRequestId}
        </p>
        <span className="justify-self-end text-[10px] font-semibold text-violet-900 bg-violet-100 px-2 py-1 rounded-full border border-violet-200/70 tabular-nums whitespace-nowrap">
          {itemBadge}
        </span>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Home className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Obra</p>
          <p className="text-xs font-semibold text-slate-800 mt-0.5 line-clamp-2 leading-snug" title={obraSectionValue}>
            {obraSectionValue}
          </p>
        </div>
      </div>

      <div className="h-px bg-slate-200/90" role="presentation" />

      <div className="flex gap-2.5">
        <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" strokeWidth={1.75} aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-slate-500 leading-tight">Data</p>
          <p className="text-xs font-semibold text-slate-800 mt-0.5 tabular-nums">
            {formatIsoDateBr(c.requestDate)}
          </p>
        </div>
      </div>

      <span
        className={
          c.stage === "suprimentos"
            ? "inline-flex self-start text-[10px] font-semibold px-2 py-1 rounded-md bg-amber-50 text-amber-900 border border-amber-200/70"
            : "inline-flex self-start text-[10px] font-semibold px-2 py-1 rounded-md bg-sky-50 text-sky-900 border border-sky-200/70"
        }
      >
        {STAGE_LABEL[c.stage]}
      </span>

      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="w-full mt-auto h-9 text-[11px] border-violet-200/90 bg-violet-50/90 text-violet-900 hover:bg-violet-100"
        onClick={onVerItens}
        leftIcon={<ListTree className="w-3.5 h-3.5 text-violet-600" />}
      >
        Ver itens
      </Button>
    </div>
  );
}

export function PipelineSolicitacoesModal({
  open,
  onClose,
  solPendente,
  solAprovada,
  itemQueryPeriod,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  solPendente: SolicitacaoCard[];
  solAprovada: SolicitacaoCard[];
  itemQueryPeriod?: { startDate: string; endDate: string };
  loading?: boolean;
}) {
  const [scModal, setScModal] = useState<SolicitacaoCard | null>(null);

  const rows = useMemo((): PipelineRow[] => {
    const a = solPendente.map((c) => ({ ...c, stage: "suprimentos" as const }));
    const b = solAprovada.map((c) => ({ ...c, stage: "gestor" as const }));
    return [...a, ...b].sort((x, y) => {
      const dx = x.requestDate || "";
      const dy = y.requestDate || "";
      if (dx !== dy) return dy.localeCompare(dx);
      return String(y.purchaseRequestId).localeCompare(String(x.purchaseRequestId), undefined, {
        numeric: true,
      });
    });
  }, [solPendente, solAprovada]);

  if (!open) return null;

  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="xl"
        closeOnEscape
        closeOnBackdropClick
        title={
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-violet-50 flex-shrink-0 border border-violet-100">
              <ClipboardList className="w-5 h-5 text-violet-600" />
            </div>
            <div className="min-w-0">
              <p className="text-base font-bold text-slate-800 leading-tight">Pipeline de solicitações</p>
              <p className="text-xs text-slate-500 truncate">
                {loading
                  ? "Carregando…"
                  : `${formatNumber(rows.length)} solicitação${rows.length === 1 ? "" : "ões"} pendente${rows.length === 1 ? "" : "s"}`}
              </p>
            </div>
          </div>
        }
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-400">
            <p className="text-sm">Consultando solicitações pendentes…</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-400">
            <Package className="w-8 h-8 opacity-40" />
            <p className="text-sm">Nenhuma solicitação pendente no período filtrado.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {rows.map((c) => (
              <PipelineCard
                key={`${c.stage}-${c.purchaseRequestId}`}
                c={c}
                onVerItens={() => setScModal(c)}
              />
            ))}
          </div>
        )}
      </Modal>

      {scModal && (
        <SolicitacaoItensModal
          solicitacao={scModal}
          onClose={() => setScModal(null)}
          itemQueryPeriod={itemQueryPeriod}
        />
      )}
    </>
  );
}
